/** @format */

import {
	Component,
	Inject,
	Input,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	AfterViewInit,
	input
} from '@angular/core';
import { selectUserStoreInfo, selectUserZipCode } from '@pdp/data-access/state';
import {
	refreshProductList,
	selectSelectedFilters,
	selectIsInitialPageLoad,
	setFiltering,
	setRefinementUpdateObj,
	setIsInitialPageLoad
} from '@plp/data-access/state';
import { shippingOptions } from '@plp/data-access/constants';
import { Store } from '@ngrx/store';
import { UtilityService, WINDOW } from '@shared/data-access/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HeaderStoreSelection } from '@shared/data-access/interfaces';
import {
	homefieldIconBuildingStore,
	HomefieldIconLibraryModule,
	homefieldIconOrderCartStandard,
	HomefieldIconsLibraryService,
	homefieldIconVehicleShipping
} from '@dsg-tech/homefield-angular-icons';
import { HomefieldTextLinkModule } from '@homefield';
import { MediaModule } from '@shared/media/components';
import { ShippingOptionButtonComponent } from '../shipping-option-button/shipping-option-button.component';
import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'plp-components-shipping-filter',
	imports: [
		NgClass,
		AsyncPipe,
		TitleCasePipe,
		HomefieldIconLibraryModule,
		HomefieldTextLinkModule,
		MediaModule,
		ShippingOptionButtonComponent,
		HmfActionTextComponent
	],
	templateUrl: './shipping-options.component.html',
	styleUrl: './shipping-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShippingOptionsComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() disabled = false;
	@Input() filtersOpen = false;
	sameDayDeliveryEnabled = input(false);
	@Input() isMobile = false;
	@Input() isInitialPageLoad = true;

	private readonly UNSUBSCRIBE$ = new Subject<void>();

	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly userZipCode$ = this.store.select(selectUserZipCode).pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly userStoreInfo$ = this.store
		.select(selectUserStoreInfo)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly isInitialPageLoad$ = this.store
		.select(selectIsInitialPageLoad)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	selectedOptions = [];
	FILTER_NAME = 'facetStore';
	shippingOptions = shippingOptions;
	shippingOptionsKeys: string[] = Object.keys(shippingOptions);
	selectedStoreInfo: HeaderStoreSelection;
	selectedUserZipCode = '';
	mobileStoreName = '';
	locationLabel = '';

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly store: Store,
		@Inject(WINDOW) protected window: Window,
		private readonly utilityService: UtilityService,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconVehicleShipping,
			homefieldIconBuildingStore,
			homefieldIconOrderCartStandard
		]);
	}

	ngOnInit(): void {
		//Filter keys: if SDD enabled, remove 'Shopping In-Store' else remove 'Same Day Delivery'
		//Ship is in it's own container if SDD flag is off.
		this.shippingOptionsKeys = this.shippingOptionsKeys.filter((key) =>
			this.sameDayDeliveryEnabled() ? key !== 'ISA' : key !== 'SDD'
		);

		this.isInitialPageLoad$.subscribe((isInitialPageLoad) => {
			this.isInitialPageLoad = isInitialPageLoad;
		});

		this.selectedFilters$.subscribe((options: Record<string, string[]>) => {
			if (options[this.FILTER_NAME]) {
				this.selectedOptions = Object.assign([], options[this.FILTER_NAME]);
				this.setMobileStoreNameAndLabel();
			} else {
				this.selectedOptions = [];
			}
		});

		this.userZipCode$.subscribe((data: string) => {
			this.selectedUserZipCode = data;
			this.setSelectedAvailablityFilter();
			this.setMobileStoreNameAndLabel();
		});

		this.userStoreInfo$.subscribe((data: HeaderStoreSelection) => {
			this.selectedStoreInfo = data;
			this.setSelectedAvailablityFilter();
			this.setMobileStoreNameAndLabel();
		});

		this.setMobileStoreNameAndLabel();
		this.cdr.detectChanges();
	}

	ngAfterViewInit(): void {
		if (this.isInitialPageLoad) {
			this.store.dispatch(setIsInitialPageLoad({ isInitialPageLoad: false }));
		}
	}

	setSelectedFilter(filterName: string): void {
		if (this.disabled) {
			return;
		}

		if (this.selectedOptions.includes(filterName)) {
			this.selectedOptions = this.selectedOptions.filter((e) => e !== filterName);
		} else {
			this.selectedOptions = [filterName];

			if (filterName === 'BOPIS' && !this.selectedStoreInfo) {
				this.openStorePanel();
			} else if (filterName !== 'BOPIS' && !this.selectedUserZipCode) {
				this.openZipCodePanel();
			}
		}
		//Check selectUserZipCode for Ship and SDD or selectedStoreInfo for PickUp exists before filetering
		if (
			(this.selectedUserZipCode && filterName !== 'BOPIS') ||
			(this.selectedStoreInfo && filterName === 'BOPIS') ||
			!this.sameDayDeliveryEnabled()
		) {
			this.setSelectedAvailablityFilter();

			//Trigger analytics event only if filter is selected
			if (this.selectedOptions.includes(filterName)) {
				this.setAnalyticsInfo(filterName);
			}
			this.store.dispatch(refreshProductList());
		}
		this.setMobileStoreNameAndLabel();

		const plpContainerElem = document.getElementById('plp-list-container');
		this.utilityService.scrollToElement(plpContainerElem, { behavior: 'instant' });
	}

	private setSelectedAvailablityFilter(): void {
		if (this.isInitialPageLoad) {
			return;
		}
		this.store.dispatch(
			setFiltering({
				selectedFilters: {
					[this.FILTER_NAME]: this.selectedOptions
				}
			})
		);
	}

	openStorePanel(): void {
		if (this.window?.headerFunctions?.storeSelection) {
			this.window.headerFunctions.storeSelection();
		}
	}

	openZipCodePanel(): void {
		if (this.window?.headerFunctions?.sameDayDelivery) {
			this.window.headerFunctions.sameDayDelivery();
		}
	}

	setAnalyticsInfo(filterName: string): void {
		const refinementUpdateObj = {
			UpdateType: 'Refinement',
			UpdateValue: {
				RefinementValue: filterName,
				RefinementAttribute: 'facetStore',
				ProductCountTotal: undefined
			}
		};
		this.store.dispatch(setRefinementUpdateObj({ refinementUpdateObj }));
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	keepOriginalOrder = (a, b) => a.key;

	/**
	 * @description
	 * Gets called on select store or delivery location link clicked on mobile
	 */
	selectUserZipCodeAndStoreInfo(): void {
		if (this.selectedOptions.includes('SHIP') || this.selectedOptions.includes('SDD')) {
			this.openZipCodePanel();
		} else {
			this.openStorePanel();
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	trackByFn(index: number, item: string): string {
		return index.toString() + item;
	}

	private setMobileStoreNameAndLabel(): void {
		if (this.selectedOptions.includes('SHIP')) {
			this.locationLabel = 'Ship To';
			this.mobileStoreName = this.selectedUserZipCode || 'Update Delivery Location';
		} else if (this.selectedOptions.includes('BOPIS')) {
			this.locationLabel = 'Pickup at';
			this.mobileStoreName = this.selectedStoreInfo?.name || 'Select Store';
		} else if (!this.sameDayDeliveryEnabled()) {
			this.locationLabel = 'My Store';
			this.mobileStoreName = this.selectedStoreInfo?.name || 'Select Store';
		} else {
			if (this.selectedOptions.includes('BOPIS')) {
				this.locationLabel = 'Pickup at';
			} else {
				this.locationLabel = this.selectedOptions.includes('SDD')
					? 'Deliver To'
					: 'Ship To';
			}
			this.mobileStoreName = this.setDeliveryLocationAndStoreInfo();
		}
	}

	private setDeliveryLocationAndStoreInfo(): string {
		if (this.selectedOptions.includes('BOPIS')) {
			return (
				this.selectedStoreInfo?.name || (this.sameDayDeliveryEnabled() && 'Select Store')
			);
		} else {
			return (
				this.selectedUserZipCode ||
				(this.sameDayDeliveryEnabled() && 'Update Delivery Location')
			);
		}
	}
}
